import {CellItem} from "../../../../model/items/cellItem";
import m, {Vnode} from "mithril";

interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string,
    readonly message: string,
}


/**
 * Speech to text component: based on web speech to text api
 *
 * DOC
 * https://developer.mozilla.org/en-US/docs/Web/API/SpeechRecognition
 *
 * TUTORIAL
 * https://tutorialzine.com/2017/08/converting-from-speech-to-text-with-javascript
 */
export default class SpeechToText {
    private recognition: SpeechRecognition|null;

    private record = false;
    private error = false;
    private sound = false;
    private audio = false;
    private speech = false;
    private allowed: boolean|null = null;

    private originalText: string|null = null;
    private speechTimeOut: number|null = null;

    private static readonly STOP_RECOGNITION_DELAY_MS = 2500;

    // android bug
    private static resultEvents: SpeechRecognitionEvent[] = [];

    // log
    private static lastResultText: string = '';


    public static isSpeechRecognitionSupported(): boolean {
        // @ts-ignore generous web api || firefox api
        return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
    }

    constructor(
        public readonly cellItem: CellItem,
        private logItem?: CellItem,
    ) {
        try {
            // @ts-ignore
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            this.recognition = new SpeechRecognition();
        } catch (e) {
            console.error(e);
            this.recognition = null
        }
        if (logItem) {
            this.setLogItem(logItem)
        }
        if (!this.recognition) {
            return;
        }

        this.recognition.continuous = true;
        this.recognition.interimResults = true;

        this.recognition.onstart = () => {
            this.record = true;
            this.allowed = true;
            this.setupOriginalText();
            this.log('Start.');

            SpeechToText.resultEvents = [];
            SpeechToText.lastResultText = '';

            m.redraw();
        }

        this.recognition.onend = () => {
            this.clearAutostop();
            this.record = false;
            this.originalText = null;
            this.log('Write ('+SpeechToText.lastResultText.length+'). End.');
            m.redraw();
        }

        this.recognition.onspeechstart = () => this.speech = true;
        this.recognition.onspeechend = () => this.speech = false;
        this.recognition.onsoundstart = () => this.sound = true;
        this.recognition.onsoundend = () => this.sound = false;
        this.recognition.onaudiostart = () => this.audio = true;
        this.recognition.onaudioend = () => this.audio = false;

        this.recognition.onerror = (e: Event) => {

            this.error = true;
            console.warn('SpeechToText: ', e);
            if ((<SpeechRecognitionErrorEvent>e).error === "not-allowed") {
                this.allowed = false
            }
            this.log(this.logItem?.getVariable()+' Error ('+(<SpeechRecognitionErrorEvent>e).error+').');
        }

        this.recognition.onresult = this.handleResult.bind(this);
    }

    public setLogItem(logItem: CellItem) {
        this.logItem = logItem;
        if (!this.recognition) {
            this.log('Unable to create speech recognition object.');
            return;
        }
        this.log('Speech recognition ready.');
    }

    private setupOriginalText(): void {
        this.originalText = (this.cellItem.getVariable() || '').trim();
        if (this.originalText?.length) {
            this.originalText += ' ';
        }
    }

    private joinResults(e: SpeechRecognitionEvent): string {
        SpeechToText.resultEvents.push(e);
        let resultText = '';

        if (SpeechToText.resultEvents.length === e.results.length) { // android bug
            resultText = e.results[e.results.length-1][0].transcript;
        } else {
            for (let i = 0; i<e.results.length; i++) {
                resultText += e.results[i][0].transcript;
            }
        }

        SpeechToText.lastResultText = resultText;
        return resultText;
    }

    private handleResult(e: SpeechRecognitionEvent): void {
        this.clearAutostop();

        const resultText = this.joinResults(e);

        this.cellItem.setVariable(this.originalText + resultText);

        this.speechTimeOut = window.setTimeout(() => {
            this.log('Autostop.');
            this.recognition?.stop();
            m.redraw();
        }, SpeechToText.STOP_RECOGNITION_DELAY_MS);
    }

    private clearAutostop(): void {
        if (this.speechTimeOut !== null) {
            clearTimeout(this.speechTimeOut);
            this.speechTimeOut = null;
        }
    }

    private get states(): string[] {
        return [
            this.record ? 'record' : 'no-record',
            this.error ? 'error' : 'no-error',
            this.sound ? 'sound' : 'no-sound',
            this.audio ? 'audio' : 'no-audio',
            this.speech ? 'speech' : 'no-speech',
            this.allowed === false ? 'not-allowed' : 'allowed',
        ];
    }

    private log(message: string): void {
        const log = this.logItem?.getVariable() || '';
        if (log?.length > 10000) {
            return;
        }
        if (log?.length > 9900) {
            message += "TRUNCATED";
        }
        this.logItem?.setVariable(log+' '+message);
    }

    public view(): Vnode|null {
        if (this.recognition === null) {
            return null;
        }
        return m('.SpeechToText', {}, m('.SpeechToText-wrapper.'+this.states.join('.'), {
            onclick: () => {
                this.record ? this.recognition?.stop() : this.recognition?.start();
            }
        }, [
            m('.microphone-background'),
            m('.microphone-foreground', {}, [
                 m('i.icon.icon-microphone'+(this.allowed===false?'-off':'')),
            ]),
        ]));
    }


}